class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('submit', (event) => {
      const discountForm = event.target.closest('[data-cart-discount-form]');
      if (!discountForm) return;

      event.preventDefault();
      this.applyDiscountCode(discountForm);
    });
    this.setHeaderCartIconAccessibility();
  }

  async applyDiscountCode(form) {
    if (form.getAttribute('aria-busy') === 'true') return;

    const input = form.querySelector('[data-cart-discount-input]');
    const button = form.querySelector('[data-cart-discount-submit]');
    const code = input?.value.trim();

    if (!code) {
      this.setDiscountMessage(form, 'Enter a coupon code.', 'error');
      input?.focus();
      return;
    }

    form.setAttribute('aria-busy', 'true');
    input?.removeAttribute('aria-invalid');
    if (button) {
      button.disabled = true;
      button.dataset.label = button.textContent;
      button.textContent = 'Applying...';
    }
    this.setDiscountMessage(form, '', '');

    try {
      const response = await fetch(routes.cart_update_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          discount: code,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname,
        }),
      });
      const cartState = await response.json();

      if (!response.ok || cartState.errors) {
        throw new Error(cartState.description || cartState.message || window.cartStrings.error);
      }

      if (!this.cartContainsDiscount(cartState, code)) {
        this.setDiscountMessage(form, 'This code could not be applied to your cart.', 'error');
        return;
      }

      await this.refreshDrawer(cartState);

      const refreshedForm = this.querySelector('[data-cart-discount-form]');
      const refreshedInput = refreshedForm?.querySelector('[data-cart-discount-input]');
      if (refreshedInput) refreshedInput.value = code;
      this.setDiscountMessage(refreshedForm, `${code} applied.`, 'success');
      refreshedInput?.focus();
    } catch (error) {
      console.error(error);
      this.setDiscountMessage(form, 'We could not apply this code. Please try again.', 'error');
    } finally {
      const currentForm = this.querySelector('[data-cart-discount-form]') || form;
      const currentButton = currentForm.querySelector('[data-cart-discount-submit]');
      currentForm.removeAttribute('aria-busy');
      if (currentButton) {
        currentButton.disabled = false;
        currentButton.textContent = currentButton.dataset.label || 'Apply';
      }
    }
  }

  cartContainsDiscount(cartState, code) {
    const titles = [];

    (cartState.cart_level_discount_applications || []).forEach((discount) => {
      titles.push(discount.title || discount.code || '');
    });

    (cartState.items || []).forEach((item) => {
      (item.discounts || []).forEach((discount) => titles.push(discount.title || ''));
      (item.line_level_discount_allocations || []).forEach((allocation) => {
        titles.push(allocation.discount_application?.title || '');
      });
    });

    const normalizedCode = code.trim().toLowerCase();
    return titles.some((title) => String(title).trim().toLowerCase() === normalizedCode);
  }

  async refreshDrawer(cartState) {
    let sectionHTML = cartState.sections?.['cart-drawer'];

    if (!sectionHTML) {
      const response = await fetch(`${routes.cart_url}?section_id=cart-drawer`);
      if (!response.ok) throw new Error(window.cartStrings.error);
      sectionHTML = await response.text();
    }

    const nextDrawer = new DOMParser().parseFromString(sectionHTML, 'text/html');
    const nextInner = nextDrawer.querySelector('#CartDrawer .drawer__inner');
    const currentInner = this.querySelector('#CartDrawer .drawer__inner');
    if (!nextInner || !currentInner) throw new Error(window.cartStrings.error);

    currentInner.replaceWith(nextInner);
    this.classList.toggle('is-empty', cartState.item_count === 0);
  }

  setDiscountMessage(form, message, type) {
    if (!form) return;
    const input = form.querySelector('[data-cart-discount-input]');
    const messageElement = form.parentElement?.querySelector('[data-cart-discount-message]');
    if (!messageElement) return;

    messageElement.textContent = message;
    messageElement.classList.toggle('is-error', type === 'error');
    messageElement.classList.toggle('is-success', type === 'success');
    input?.toggleAttribute('aria-invalid', type === 'error');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
